import React, { useEffect, useRef } from 'react';
import { Card, Box, Grid, Button, IconButton, Divider, useTheme, CircularProgress, Tooltip } from '@mui/material';
import HeaderTilteBar from 'components/TitleBar/HeaderTilteBar';
import MainContainer from 'theme/MainConatiner';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FieldArray, FormikProvider } from 'formik';
import CustomTextField from 'components/Fileds/CustomTextFiled';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomAutocomplete from 'components/Fileds/CustomAutocomplete';
import CustomTextArea from 'components/Fileds/CustomTextArea';
import ImageDropzone from 'components/DropZone/DropzoneComponent';
import { useSelector } from 'react-redux';
import CustomButton from 'components/Fileds/CustomButton';
import { currencySymbol } from 'utils/constant';
import CustomSelect from 'components/Fileds/CustomSelect';
import { useAutoFocusFirstField } from 'utils/focusHelper';


const AddFoodItemsContainer = ({ formik, loading, catOptions,
    cpOptions,
    itemOptions,
    // chefOptions, 
    stationsOptions,
    // collectionPointsOptions,
    // loadingCollectionPoint,
    setSaveClick,
    deletedHelpers: { setDeletedCP, setDeletedGroups, setDeletedAddOns },
    saveClick,
    isEdit,
    openStationModal,
    openCategoryModal
}) => {
    const { values, handleSubmit, handleChange, handleBlur, setFieldValue, errors, touched } = formik;
    const formRef = useAutoFocusFirstField([isEdit]);

    const theme = useTheme();
    const userData = useSelector((state) => state?.posData)
    // Filter out selected primary chef from secondary options
    // const secondaryChefOptions = chefOptions?.filter(
    //     (chef) => chef.value !== values?.primaryChef?.value
    // );

    // Keep previous selected CPs for edit delete tracking
    // const prevCPRef = useRef(values.collectionPoints);
    // useEffect(() => {
    //     if (isEdit) {
    //         prevCPRef.current = values.collectionPoints;
    //     }
    // }, [values.collectionPoints, isEdit]);

    return (
        <MainContainer>
            <Card className="main-card-container">
                <HeaderTilteBar title={isEdit ? "Edit Food Item" : "Add Food Item"} isSearch={false} isAddVisible={false} />
                <FormikProvider value={formik}>
                    <Box component="form" ref={formRef} onSubmit={handleSubmit} sx={{ mt: 3, px: 4, pb: 4 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <CustomTypography variant="subtitle2" label="Food Item Image"
                                    sx={{ fontSize: '14px', fontWeight: 600 }}
                                />
                                <ImageDropzone fieldName="image" formik={formik} isEdit={isEdit} />
                            </Grid>


                            {/* Right Column: All other fields in 8-col grid */}
                            <Grid item xs={12} md={8}>
                                <Grid container spacing={2}>
                                    {/* Row 1: Name & Description */}
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ mb: 2 }}>
                                            <CustomTypography variant="subtitle2" label="Item Name *"
                                                sx={{ fontSize: '14px', fontWeight: 600 }}
                                            />
                                            <CustomTextField
                                                fullWidth
                                                name="name"
                                                placeholder="Enter Food Item Name"
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={Boolean(touched.name && errors.name)}
                                                helperText={touched.name && errors.name}
                                            />
                                        </Box>
                                        <Box>
                                            <Box className='d-flex align-items-center' gap={1} sx={{ mb: 1 }}>
                                                <CustomTypography variant="subtitle2" label="Item Category *"
                                                    sx={{ fontSize: '14px', fontWeight: 600, mb: 0 }}
                                                />
                                                <Tooltip title="Add Category" arrow placement="top">
                                                    <IconButton
                                                        size="small"
                                                        onClick={openCategoryModal}
                                                        sx={{
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: theme.palette.common.white,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.primary.dark,
                                                            },
                                                            width: 20,
                                                            height: 20,
                                                            ml: 1
                                                        }}
                                                    >
                                                        <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            <CustomAutocomplete
                                                fullWidth
                                                options={catOptions}
                                                // disabled={loadingCats}
                                                value={values.category}
                                                onChange={(_, val) => setFieldValue('category', val)}
                                                placeholder="Select Food Item Category"
                                                error={Boolean(touched.category && errors.category)}
                                                helperText={touched.category && errors.category}
                                                onBlur={handleBlur}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CustomTypography variant="subtitle2" label="Item Description"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomTextArea
                                            fullWidth
                                            name="description"
                                            placeholder="Enter food item description here"
                                            value={values.description}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.description && errors.description)}
                                            helperText={touched.description && errors.description}
                                            minRows={6}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <CustomTypography variant="subtitle2" label="Item Price *"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomTextField
                                            fullWidth
                                            name="price"
                                            placeholder="Enter Food Item Price"
                                            value={values.price}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.price && errors.price)}
                                            helperText={touched.price && errors.price}
                                            InputProps={{ startAdornment: <Box component="span" mr={1}>{currencySymbol(userData?.vendor?.currency)}</Box> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CustomTypography variant="subtitle2" label="Food Item Type *"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomAutocomplete
                                            fullWidth
                                            options={itemOptions}
                                            value={values.type}
                                            onChange={(_, val) => setFieldValue('type', val)}
                                            placeholder="Select Item Type"
                                            error={Boolean(touched.type && errors.type)}
                                            helperText={touched.type && errors.type}
                                            onBlur={handleBlur}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Row 3: Collection & Chefs */}
                            <Grid item xs={12} md={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <CustomTypography variant="subtitle2" label="Collection Point"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomAutocomplete
                                            fullWidth
                                            multiple
                                            options={cpOptions}
                                            filterSelectedOptions
                                            // value={values.collectionPoints}
                                            value={values.collectionPoints ?? []}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            // onChange={(_, val) => setFieldValue('collectionPoints', val)}
                                            onChange={(e, newVal, reason, details) => {
                                                // Track deletions
                                                if (isEdit && reason === 'removeOption' && details?.option) {
                                                    setDeletedCP(prev => [...prev, details.option.value]);
                                                }
                                                setFieldValue('collectionPoints', newVal);
                                            }}
                                            // placeholder="Select Collection Point(s)"
                                             placeholder={
                                                values.collectionPoints && values.collectionPoints.length > 0
                                                    ? ''                     // once you’ve picked one, clear the placeholder
                                                    : 'Select Collection Point(s)'
                                            }
                                            error={Boolean(touched.collectionPoints && errors.collectionPoints)}
                                            helperText={touched.collectionPoints && errors.collectionPoints}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box className='d-flex align-items-center' gap={1} sx={{ mb: 1 }}>
                                            <CustomTypography variant="subtitle2" label="Stations *"
                                                sx={{ fontSize: '14px', fontWeight: 600, mb: 0 }}
                                            />
                                            <Tooltip title="Add Station" arrow placement="top">
                                                <IconButton
                                                    size="small"
                                                    onClick={openStationModal}
                                                    sx={{
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: theme.palette.common.white,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.primary.dark,
                                                        },
                                                        width: 20,
                                                        height: 20,
                                                        ml: 1
                                                    }}
                                                >
                                                    <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <CustomAutocomplete
                                            fullWidth
                                            options={stationsOptions}
                                            value={values.station}
                                            onChange={(_, val) => setFieldValue('station', val)}
                                            placeholder="Select Station"
                                            error={Boolean(touched.station && errors.station)}
                                            onBlur={handleBlur}
                                            helperText={touched.station && errors.station}
                                        />
                                    </Grid>
                                    {/* <Grid item xs={12} sm={4}>
                                        <CustomTypography variant="subtitle2" label="Collection Point"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomSelect
                                            name="collectionPoint"
                                            value={values.collectionPoint}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            multiple={true}
                                            placeholder="Select Collection Points"
                                            options={collectionPointsOptions}
                                            error={touched.collectionPoint && errors.collectionPoint}
                                            helperText={touched.collectionPoint && errors.collectionPoint}
                                            disabled={loadingCollectionPoint}
                                        />
                                    </Grid> */}
                                    <Grid item xs={12} sm={4}>
                                        <CustomTypography variant="subtitle2" label="Average preparation time (In minutes) *"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomTextField
                                            fullWidth
                                            name="averagePreparationTime"
                                            placeholder="Enter Avg. preparation time"
                                            value={values.averagePreparationTime}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={Boolean(touched.averagePreparationTime && errors.averagePreparationTime)}
                                            helperText={touched.averagePreparationTime && errors.averagePreparationTime}
                                        />
                                    </Grid>
                                    {/* <Grid item xs={12} sm={4}>
                                        <CustomTypography variant="subtitle2" label="Primary Chef"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomAutocomplete
                                            fullWidth
                                            options={chefOptions}
                                            value={values.primaryChef}
                                            onChange={(_, val) => setFieldValue('primaryChef', val)}
                                            placeholder="Select Primary Chef"
                                            error={Boolean(touched.primaryChef && errors.primaryChef)}
                                            onBlur={handleBlur}
                                            helperText={touched.primaryChef && errors.primaryChef}
                                        />
                                    </Grid> */}
                                    {/* <Grid item xs={12} sm={4}>
                                        <CustomTypography variant="subtitle2" label="Secondary Chef"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        />
                                        <CustomAutocomplete
                                            fullWidth
                                            options={secondaryChefOptions}
                                            value={values.secondaryChef}
                                            disabled={!values.primaryChef}
                                            onChange={(_, val) => setFieldValue('secondaryChef', val)}
                                            placeholder="Select Secondary Chef"
                                            error={Boolean(touched.secondaryChef && errors.secondaryChef)}
                                            helperText={touched.secondaryChef && errors.secondaryChef}
                                            onBlur={handleBlur}
                                        />
                                    </Grid> */}
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ color: "#CBCBCB", margin: '20px 0px', marginBottom: '5px' }} />
                            </Grid>

                            {/* Add-ons Section */}
                            <Grid item xs={12}>
                                <Box className='d-flex align-items-center' gap={1} sx={{ mb: 2 }}>
                                    <CustomTypography variant="subtitle2" label="Add-ons for Food Item"
                                        sx={{ fontSize: '20px', fontWeight: 700 }}
                                    />
                                    <CustomTypography variant={"subtitle2"}
                                        label={"(Optional)"}
                                        sx={{ fontSize: '20px', fontWeight: 400 }}
                                    />
                                </Box>
                                <FieldArray name="addOnGroups">
                                    {({ push, remove }) => (
                                        <>
                                            {values.addOnGroups.map((group, gIdx) => (
                                                <Box key={gIdx} sx={{ border: '1px solid', borderColor: '#CBCBCB', p: 3, mb: 2, borderRadius: 1, backgroundColor: '#F9F9F9' }}>
                                                    <Grid container spacing={2} alignItems="flex-start">
                                                        <Grid item xs={12} sm={4}>
                                                            <CustomTypography variant="body2" label="Group Name"
                                                                sx={{ fontSize: '14px', fontWeight: 600 }}
                                                            />
                                                            <CustomTextField
                                                                name={`addOnGroups[${gIdx}].groupName`}
                                                                value={group.groupName}
                                                                onChange={handleChange}
                                                                placeholder={"Enter Add-on group name"}
                                                                onBlur={handleBlur}
                                                                error={touched.addOnGroups?.[gIdx]?.groupName && Boolean(errors.addOnGroups?.[gIdx]?.groupName)}
                                                                helperText={touched.addOnGroups?.[gIdx]?.groupName && errors.addOnGroups?.[gIdx]?.groupName}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <CustomTypography variant="body2" label="Selection Type"
                                                                sx={{ fontSize: '14px', fontWeight: 600 }}
                                                            />
                                                            <CustomSelect
                                                                name={`addOnGroups[${gIdx}].groupType`}
                                                                options={[
                                                                    { label: 'Single', value: 'single' },
                                                                    { label: 'Multiple', value: 'multiple' }
                                                                ]}
                                                                value={group.groupType ? String(group.groupType).toLowerCase() : ''}
                                                                onChange={(e) => setFieldValue(`addOnGroups[${gIdx}].groupType`, e.target.value)}
                                                                placeholder="Select Group Type"
                                                                renderValue={(selected) => {
                                                                    if (!selected) {
                                                                        return <span style={{ color: 'gray' }}>Select Group Type</span>;
                                                                    }
                                                                    const match = [
                                                                        { label: 'Single', value: 'single' },
                                                                        { label: 'Multiple', value: 'multiple' }
                                                                    ].find(o => o.value === selected);
                                                                    return match ? match.label : (selected.charAt(0).toUpperCase() + selected.slice(1));
                                                                }}
                                                                error={touched.addOnGroups?.[gIdx]?.groupType && Boolean(errors.addOnGroups?.[gIdx]?.groupType)}
                                                                helperText={touched.addOnGroups?.[gIdx]?.groupType && errors.addOnGroups?.[gIdx]?.groupType}
                                                                onBlur={handleBlur}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
                                                            <IconButton type="button" color="error"
                                                                onClick={() => {
                                                                    // Track deleted group
                                                                    if (isEdit && group.id) {
                                                                        setDeletedGroups(prev => [...prev, group.id]);
                                                                    }
                                                                    remove(gIdx);
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Grid>
                                                    </Grid>

                                                    <FieldArray name={`addOnGroups[${gIdx}].addOns`}>
                                                        {({ push: pushAddOn, remove: removeAddOn }) => (
                                                            <>
                                                                {group.addOns.map((addOn, aIdx) => (
                                                                    <Grid container spacing={2} alignItems="center" key={aIdx} sx={{ mt: 1 }}>
                                                                        <Grid item xs={12} sm={4}>
                                                                            <CustomTypography variant="body2" label="Add-on Name"
                                                                                sx={{ fontSize: '14px', fontWeight: 600 }}
                                                                            />
                                                                            <CustomTextField
                                                                                name={`addOnGroups[${gIdx}].addOns[${aIdx}].name`}
                                                                                value={addOn.name}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                placeholder={"Enter add-on Name"}
                                                                                error={Boolean(touched.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.name && errors.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.name)}
                                                                                helperText={touched.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.name && errors.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.name}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={4}>
                                                                            <CustomTypography variant="body2" label="Price"
                                                                                sx={{ fontSize: '14px', fontWeight: 600 }}
                                                                            />
                                                                            <CustomTextField
                                                                                name={`addOnGroups[${gIdx}].addOns[${aIdx}].price`}
                                                                                value={addOn.price}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                placeholder={"Enter price for Add-on"}
                                                                                error={Boolean(touched.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.price && errors.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.price)}
                                                                                helperText={touched.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.price && errors.addOnGroups?.[gIdx]?.addOns?.[aIdx]?.price}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={3}>
                                                                            <CustomTypography variant="body2" label="Notes"
                                                                                sx={{ fontSize: '14px', fontWeight: 600 }}
                                                                            />
                                                                            <CustomTextField
                                                                                name={`addOnGroups[${gIdx}].addOns[${aIdx}].notes`}
                                                                                value={addOn.notes}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                placeholder={"Enter any notes here"}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={1} sx={{ textAlign: 'right', marginTop: '10px' }}>
                                                                            {aIdx === group.addOns.length - 1 ? (
                                                                                <IconButton type="button"
                                                                                    sx={{ backgroundColor: theme.palette.primary.main, borderRadius: '5px' }}
                                                                                    onClick={() => pushAddOn({ name: '', price: '', notes: '' })}>
                                                                                    <AddIcon sx={{ color: theme.palette.secondary.main }} />
                                                                                </IconButton>
                                                                            ) : (
                                                                                <IconButton type="button" color="error"
                                                                                    onClick={() => {
                                                                                        // Track deleted add-on
                                                                                        if (isEdit && addOn.id) {
                                                                                            setDeletedAddOns(prev => [...prev, addOn.id]);
                                                                                        }
                                                                                        removeAddOn(aIdx);
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon />
                                                                                </IconButton>
                                                                            )}
                                                                        </Grid>
                                                                    </Grid>
                                                                ))}
                                                            </>
                                                        )}
                                                    </FieldArray>

                                                </Box>
                                            ))}
                                            <Button
                                                type="button"
                                                startIcon={<AddIcon />}
                                                onClick={() => push({ groupName: '', groupType: null, addOns: [{ name: '', price: '', notes: '' }] })}
                                                sx={{ textTransform: 'none', mt: 1, color: theme.palette.highlight.main }}
                                            >
                                                Add New Group
                                            </Button>
                                        </>
                                    )}
                                </FieldArray>
                            </Grid>

                            {/* Actions */}
                            <Grid item xs={12} sx={{ textAlign: 'right', mt: 3 }}>
                                {!isEdit &&
                                    <CustomButton type="submit" variant="outlined" disabled={loading} sx={{
                                        mr: 2,
                                        backgroundColor: "transparent",
                                        border: '1px solid black',
                                        color: 'black'
                                    }}>
                                        {/* Save & Add New */}
                                        {loading && !saveClick
                                            ? <CircularProgress size={20} thickness={4} sx={{ color: 'white' }} />
                                            : 'Save & Add New'}
                                    </CustomButton>
                                }
                                <CustomButton type="submit" variant="contained" onClick={() => setSaveClick(true)} disabled={loading}>
                                    {/* Save */}
                                    {loading && saveClick
                                        ? <CircularProgress size={20} thickness={4} sx={{ color: 'white' }} />
                                        : 'Save'}
                                </CustomButton>
                            </Grid>
                        </Grid>
                    </Box>
                </FormikProvider>
            </Card>
        </MainContainer>
    );
};

export default AddFoodItemsContainer;
